import {PageTitle} from 'layout/master-layout'
import TutorialCard from './TutorialCard'
import {TutorialSection, TutorialTopic} from 'models/Ticket'
import {useCallback, useEffect, useState} from 'react'
import {
 fetchUserCategoryDropdown,
 getTutorialsApi,
} from 'utils/api/auth/ticketService'
import {Spinner} from 'helpers'
import {useTranslation} from 'react-i18next'
import {IFormField} from 'models/Common'
import {USER_CATEGORY} from 'constants/userCategory'

const ROLE_GROUPS = {
 PMU: USER_CATEGORY.PMU_BAYER,
 DISTRIBUTOR: USER_CATEGORY.DISTRIBUTOR_DIRECTOR,
 TSU: USER_CATEGORY.TSU_USER,
} as const

const QUICK_FILTER_VISIBLE_ROLES = [ROLE_GROUPS.PMU]

export default function TutorialWrapper() {
 const {t} = useTranslation()
 const [tutorials, setTutorials] = useState<
  TutorialSection[]
 >([])
 const [nextUrl, setNextUrl] = useState<string | null>(null)
 const [loading, setLoading] = useState(false)
 const userAccess = localStorage.getItem('user')
 const userCategoryKey = userAccess
  ? JSON.parse(userAccess)?.userCategoryKey
  : null
 const [quickFilters, setQuickFilters] = useState<
  {label: string; value: string}[]
 >([])
 const userCategory = userAccess
  ? JSON.parse(userAccess)?.userCategory
  : null
 const [filterFormData, setFilterFormData] =
  useState<IFormField | null>(null)

 const loadTutorials = async (
  url?: string,
  filters?: IFormField | null
 ) => {
  if (loading) return

  setLoading(true)

  const payload = {
   ...(filters || {}),
  }

  const res = await getTutorialsApi(url, payload)

  if (url) {
   setTutorials((prev) => [...prev, ...res.results])
  } else {
   setTutorials(res.results)
  }

  setNextUrl(res.next)
  setLoading(false)
 }

 const getQuickFilters = async () => {
  setLoading(true)

  const res = await fetchUserCategoryDropdown()

  const normalize = (val: string) =>
   String(val || '')
    .toLowerCase()
    .trim()

  let updatedFilters = res

  if (
   userCategory &&
   QUICK_FILTER_VISIBLE_ROLES.includes(userCategoryKey)
  ) {
   const matched = res?.find(
    (item) => item.label === userCategory
   )

   if (matched) {
    updatedFilters = [
     matched,
     ...res.filter(
      (item) =>
       normalize(item.value) !== normalize(matched.value)
     ),
    ]

    const defaultFilter = {
     user_category: matched.value,
    }

    setFilterFormData(defaultFilter)
   }
  }

  setQuickFilters(updatedFilters)
  setLoading(false)
 }

 useEffect(() => {
  void getQuickFilters()
 }, [])

 const handleScroll = useCallback(() => {
  if (!nextUrl || loading) return

  const scrollPosition =
   window.innerHeight + document.documentElement.scrollTop

  const pageHeight = document.documentElement.offsetHeight

  if (scrollPosition >= pageHeight - 100) {
   void loadTutorials(nextUrl, filterFormData)
  }
 }, [nextUrl, loading, filterFormData])

 useEffect(() => {
  window.addEventListener('scroll', handleScroll)

  return () =>
   window.removeEventListener('scroll', handleScroll)
 }, [handleScroll])

 useEffect(() => {
  if (filterFormData) {
   setTutorials([])
   setNextUrl(null)

   void loadTutorials(undefined, filterFormData)
  } else {
   void loadTutorials()
  }
 }, [filterFormData])

 return (
  <>
   {loading && (
    <div className='fixed inset-0 z-[9999] flex items-center justify-center bg-black/30'>
     <Spinner />
    </div>
   )}
   <div className='container relative'>
    <div className='px-[72px] pt-6 pb-10 bg-white w-full min-h-full'>
     <PageTitle
      breadcrumbContainerClass='flex text-[#5A6066] text-sm font-normal items-center pb-6 border-[#E6EBF0]'
      breadcrumbs={[
       {
        title: t('breadCrumbs.home'),
        path: '/home-page',
        isActive: false,
       },
       {
        title: t('breadCrumbs.tutorials'),
        path: '/tutorials',
        isActive: true,
       },
      ]}
     >
      Tutorials
     </PageTitle>
     {QUICK_FILTER_VISIBLE_ROLES.includes(
      userCategoryKey
     ) && (
      <>
       {quickFilters?.length > 0 && (
        <div className='flex items-center gap-4 bg-[#F5F7FA] px-4 py-3 rounded-xl mb-6'>
         <span className='text-sm text-[#5A6066] font-medium whitespace-nowrap'>
          {t('common.quickFilters')}
         </span>

         <div className='flex gap-3 overflow-x-auto scrollbar-hide'>
          {quickFilters.map((item) => {
           const isActive =
            filterFormData?.user_category === item.value

           return (
            <button
             key={item.value}
             className={`px-5 py-2 rounded-xl text-sm font-medium whitespace-nowrap border transition-all ${
              isActive
               ? 'bg-[#4F7DF3] text-white border-[#4F7DF3]'
               : 'bg-white text-[#5A6066] border-[#D0D5DD]'
             }`}
             onClick={() => {
              let updatedFilters = null

              if (!isActive) {
               updatedFilters = {
                user_category: item.value,
               }
              }

              setFilterFormData(updatedFilters)
             }}
            >
             {item.label.toUpperCase()}
            </button>
           )
          })}
         </div>
        </div>
       )}
      </>
     )}
     {tutorials?.map(
      (section: TutorialSection, index: number) => (
       <div key={index} className='mb-10'>
        <h2 className='text-sm font-semibold text-[#2D3339] mb-4'>
         {section.heading}
        </h2>
        <div className='flex gap-6 bg-[#F5F7FA] p-6 rounded-xl overflow-x-auto scrollbar-hide hide-scrollbar'>
         {section.topics
          .sort(
           (a: TutorialTopic, b: TutorialTopic) =>
            Number(a.sequence) - Number(b.sequence)
          )
          .map((topic: TutorialTopic) => (
           <div
            key={topic.sequence}
            className='flex-shrink-0 w-[calc((100%-48px)/3)]'
           >
            <TutorialCard
             key={topic.sequence}
             title={topic.title}
             description={topic.description}
             duration={topic.duration}
             url={topic.url}
             type={topic.type}
            />
           </div>
          ))}
        </div>
       </div>
      )
     )}
    </div>
   </div>
  </>
 )
}
